import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = ["admin", "leadership", "command_center", "ado", "pod_lead", "installer"];

export default function Admin() {
  const { user } = useAuth();
  if (!user || user.role !== "admin") {
    return (
      <Card className="p-8 text-center" data-testid="admin-locked">
        <h3 className="font-display text-xl font-semibold">Admin only</h3>
        <p className="text-sm text-zinc-500 mt-1">Sign in as the admin user to access this page.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-5" data-testid="admin-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Master Data</p>
        <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Admin</h2>
      </div>
      <Tabs defaultValue="users">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="cities" data-testid="tab-cities">Cities</TabsTrigger>
          <TabsTrigger value="pods" data-testid="tab-pods">Pods</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersPanel /></TabsContent>
        <TabsContent value="contacts" className="mt-4"><ContactsPanel /></TabsContent>
        <TabsContent value="cities" className="mt-4"><CitiesPanel /></TabsContent>
        <TabsContent value="pods" className="mt-4"><PodsPanel /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const refresh = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="overflow-hidden" data-testid="users-panel">
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="font-display text-base font-semibold">Users ({users.length})</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-user-button">
              <Plus className="h-4 w-4 mr-1" /> Add user
            </Button>
          </DialogTrigger>
          <NewUserDialog onClose={() => setOpen(false)} onCreated={refresh} />
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
          <tr>
            <th className="text-left py-2.5 px-4 font-semibold">Email</th>
            <th className="text-left py-2.5 px-4 font-semibold">Name</th>
            <th className="text-left py-2.5 px-4 font-semibold">Role</th>
            <th className="text-left py-2.5 px-4 font-semibold">Created</th>
            <th className="text-left py-2.5 px-4 font-semibold w-12"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${u.id}`}>
              <td className="py-2.5 px-4 font-medium">{u.email}</td>
              <td className="py-2.5 px-4 text-zinc-600">{u.name}</td>
              <td className="py-2.5 px-4 capitalize"><span className="inline-block px-2 py-0.5 rounded bg-zinc-100 text-zinc-800 text-xs">{u.role.replace("_", " ")}</span></td>
              <td className="py-2.5 px-4 font-mono text-xs text-zinc-500">{(u.created_at || "").slice(0, 10)}</td>
              <td className="py-2.5 px-4">
                <button onClick={() => remove(u.id)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-user-${u.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function NewUserDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "ado", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <DialogContent data-testid="new-user-dialog">
      <DialogHeader><DialogTitle className="font-display">Add user</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
        </div>
        <div>
          <Label className="text-xs">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
        </div>
        <div>
          <Label className="text-xs">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Initial password</Label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="new-user-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-user-submit">
          {submitting ? "Saving…" : "Create user"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function CitiesPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", zone: "" });
  const refresh = () => api.get("/masters/cities").then((r) => setItems(r.data));
  useEffect(() => { refresh(); }, []);
  const submit = async () => {
    try { await api.post("/masters/cities", form); toast.success("City added"); setForm({ name: "", code: "", zone: "" }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Delete city ${name}?`)) return;
    try { await api.delete(`/masters/cities/${id}`); toast.success("City deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="overflow-hidden" data-testid="cities-panel">
      <div className="px-5 py-3 border-b border-zinc-200">
        <h3 className="font-display text-base font-semibold">Cities ({items.length})</h3>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 border-b border-zinc-200">
        <Input placeholder="Name (e.g. Mysore)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-city-name" />
        <Input placeholder="Code (e.g. MYS)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="new-city-code" />
        <Input placeholder="Zone" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} data-testid="new-city-zone" />
        <Button onClick={submit} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-city-submit"><Plus className="h-4 w-4 mr-1" /> Add city</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
          <tr>
            <th className="text-left py-2.5 px-4 font-semibold">Name</th>
            <th className="text-left py-2.5 px-4 font-semibold">Code</th>
            <th className="text-left py-2.5 px-4 font-semibold">Zone</th>
            <th className="text-left py-2.5 px-4 font-semibold w-12"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`city-row-${c.id}`}>
              <td className="py-2.5 px-4 font-medium">{c.name}</td>
              <td className="py-2.5 px-4 font-mono text-xs">{c.code}</td>
              <td className="py-2.5 px-4 text-zinc-600">{c.zone}</td>
              <td className="py-2.5 px-4">
                <button onClick={() => remove(c.id, c.name)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-city-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PodsPanel() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ name: "", city: "", lead: "" });
  const refresh = () => api.get("/masters/pods").then((r) => setItems(r.data));
  useEffect(() => {
    refresh();
    api.get("/masters/cities").then((r) => setCities(r.data));
  }, []);
  const submit = async () => {
    try { await api.post("/masters/pods", form); toast.success("Pod added"); setForm({ name: "", city: "", lead: "" }); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Delete pod ${name}?`)) return;
    try { await api.delete(`/masters/pods/${id}`); toast.success("Pod deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="overflow-hidden" data-testid="pods-panel">
      <div className="px-5 py-3 border-b border-zinc-200">
        <h3 className="font-display text-base font-semibold">Pods ({items.length})</h3>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 border-b border-zinc-200">
        <Input placeholder="Pod name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-pod-name" />
        <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
          <SelectTrigger data-testid="new-pod-city"><SelectValue placeholder="City" /></SelectTrigger>
          <SelectContent>
            {cities.filter((c) => c.name).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Lead" value={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.value })} data-testid="new-pod-lead" />
        <Button onClick={submit} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-pod-submit"><Plus className="h-4 w-4 mr-1" /> Add pod</Button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
          <tr>
            <th className="text-left py-2.5 px-4 font-semibold">Pod</th>
            <th className="text-left py-2.5 px-4 font-semibold">City</th>
            <th className="text-left py-2.5 px-4 font-semibold">Lead</th>
            <th className="text-left py-2.5 px-4 font-semibold w-12"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`pod-row-${p.id}`}>
              <td className="py-2.5 px-4 font-medium">{p.name}</td>
              <td className="py-2.5 px-4 text-zinc-600">{p.city}</td>
              <td className="py-2.5 px-4 text-zinc-600">{p.lead}</td>
              <td className="py-2.5 px-4">
                <button onClick={() => remove(p.id, p.name)} className="text-zinc-400 hover:text-red-600" data-testid={`delete-pod-${p.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}


function AuditPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const params = filter === "all" ? {} : { entity_type: filter };
    api.get("/audit", { params }).then((r) => setItems(r.data));
  }, [filter]);
  return (
    <Card className="overflow-hidden" data-testid="audit-panel">
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
        <h3 className="font-display text-base font-semibold">Audit log ({items.length})</h3>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            <SelectItem value="campaign">Campaigns</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="planning">Planning</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
          <tr>
            <th className="text-left py-2.5 px-4 font-semibold">When</th>
            <th className="text-left py-2.5 px-4 font-semibold">Entity</th>
            <th className="text-left py-2.5 px-4 font-semibold">ID</th>
            <th className="text-left py-2.5 px-4 font-semibold">Action</th>
            <th className="text-left py-2.5 px-4 font-semibold">By</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-zinc-500 text-xs">No audit entries.</td></tr>}
          {items.map((a) => (
            <tr key={a.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`audit-row-${a.id}`}>
              <td className="py-2.5 px-4 font-mono text-xs">{(a.updated_at || "").slice(0, 19).replace("T", " ")}</td>
              <td className="py-2.5 px-4 capitalize">{a.entity_type}</td>
              <td className="py-2.5 px-4 font-mono text-xs">{a.entity_id}</td>
              <td className="py-2.5 px-4">
                <span className={`px-2 py-0.5 rounded text-xs ${a.action === "create" ? "bg-emerald-50 text-emerald-700" : a.action === "delete" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>
                  {a.action}
                </span>
              </td>
              <td className="py-2.5 px-4 text-zinc-600 text-xs">{a.updated_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

const CONTACT_ROLES = ["ado", "pod_lead", "command_center", "leadership", "sales", "ops", "installer", "other"];

function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [filterRole, setFilterRole] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const refresh = () => {
    const params = filterRole === "all" ? {} : { role: filterRole };
    api.get("/contacts", { params }).then((r) => {
      setItems(r.data);
      setSelectedIds(new Set());
    });
  };
  useEffect(() => { refresh(); }, [filterRole]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((c) => c.id)));
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete contact ${name}?`)) return;
    try { await api.delete(`/contacts/${id}`); toast.success("Contact deleted"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} contact${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/contacts/bulk-delete", { contact_ids: ids });
      toast.success(`Deleted ${data.deleted} contact${data.deleted === 1 ? "" : "s"}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  return (
    <Card className="overflow-hidden" data-testid="contacts-panel">
      <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-base font-semibold">Escalation contacts ({items.length})</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Notifications resolve role tags (ADO / Pod Lead / Command / Leadership) to these emails & phones.</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-contacts-button"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
            </Button>
          )}
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-44 text-xs" data-testid="filter-contact-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {CONTACT_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="new-contact-button">
                <Plus className="h-4 w-4 mr-1" /> Add contact
              </Button>
            </DialogTrigger>
            <ContactDialog onClose={() => setOpen(false)} onSaved={refresh} />
          </Dialog>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
            <tr>
              <th className="text-left py-2.5 px-3 w-10">
                <Checkbox
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-contacts"
                />
              </th>
              <th className="text-left py-2.5 px-4 font-semibold">Name</th>
              <th className="text-left py-2.5 px-4 font-semibold">Email</th>
              <th className="text-left py-2.5 px-4 font-semibold">Phone</th>
              <th className="text-left py-2.5 px-4 font-semibold">Role</th>
              <th className="text-left py-2.5 px-4 font-semibold">Active</th>
              <th className="text-left py-2.5 px-4 font-semibold w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-xs">No contacts. Add the first one →</td></tr>}
            {items.map((c) => (
              <tr key={c.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${!c.active ? "opacity-60" : ""}`} data-testid={`contact-row-${c.id}`}>
                <td className="py-2.5 px-3">
                  <Checkbox
                    checked={selectedIds.has(c.id)}
                    onCheckedChange={() => toggleSelect(c.id)}
                    data-testid={`select-contact-${c.id}`}
                  />
                </td>
                <td className="py-2.5 px-4 font-medium">{c.name}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{c.email}</td>
                <td className="py-2.5 px-4 font-mono text-xs text-zinc-600">{c.phone || "—"}</td>
                <td className="py-2.5 px-4">
                  <span className="inline-block px-2 py-0.5 rounded bg-zinc-100 text-zinc-800 text-xs capitalize">{c.role.replace("_", " ")}</span>
                </td>
                <td className="py-2.5 px-4">
                  {c.active ? <span className="text-emerald-700 text-xs">● Active</span> : <span className="text-zinc-400 text-xs">○ Inactive</span>}
                </td>
                <td className="py-2.5 px-4">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(c)} className="text-zinc-500 hover:text-zinc-950 p-1" data-testid={`edit-contact-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(c.id, c.name)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`delete-contact-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <ContactDialog contact={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
        </Dialog>
      )}
    </Card>
  );
}

function ContactDialog({ contact, onClose, onSaved }) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    name: contact?.name || "",
    email: contact?.email || "",
    phone: contact?.phone || "",
    role: contact?.role || "ado",
    city: contact?.city || "",
    pod: contact?.pod || "",
    active: contact?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/contacts/${contact.id}`, form);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", form);
        toast.success("Contact added");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <DialogContent data-testid="contact-dialog">
      <DialogHeader><DialogTitle className="font-display">{isEdit ? "Edit" : "Add"} escalation contact</DialogTitle></DialogHeader>
      <p className="text-xs text-zinc-500 -mt-2">
        For role <span className="font-mono">ado</span> / <span className="font-mono">pod_lead</span>: name MUST match the campaign's ADO / Pod Lead text exactly so the system can route alerts to the right person.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div className="sm:col-span-2">
          <Label className="text-xs">Full name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
        </div>
        <div>
          <Label className="text-xs">Phone (E.164, e.g. +91…)</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" />
        </div>
        <div>
          <Label className="text-xs">Role</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="contact-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTACT_ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">City (optional)</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="contact-city" />
        </div>
        <div>
          <Label className="text-xs">Pod (optional)</Label>
          <Input value={form.pod} onChange={(e) => setForm({ ...form, pod: e.target.value })} data-testid="contact-pod" />
        </div>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer mt-1">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="contact-active" />
          <span>Active (receives notifications)</span>
        </label>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="contact-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="contact-submit">
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add contact"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

